import React, { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import { Card, Button, ProgressBar, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  Quiz,
  TileMatchPlayStart,
  TileMatchTileDto,
  TileMatchPlayState,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  authService,
  quizService,
  TileMatchFinishAudit,
  HostedOpenAsyncPvPSummary,
  pvpService,
} from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useTranslation } from 'react-i18next';
import {
  quizQuestionImageUrlsFromKey,
  resolveTileMatchImageAssetsFolder,
} from '../utils/quizQuestionImageSrc';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import {
  buildSoloArenaHostCompleteBody,
  type SoloArenaPublishSnapshot,
} from '../utils/soloArenaPublishLayout';
import { normalizePlayDifficultyLabel } from '../utils/quizPlayBandScoring';
import { isPwaStandalone } from '../utils/pwaStandalone';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import {
  TILE_MATCH_TOTAL_ROUNDS,
  TILE_MATCH_BAR_CAPACITY,
  TILE_MATCH_TIME_LIMIT_SECONDS,
  TILE_MATCH_TRIO_TIME_BONUS_SECONDS,
  tileMatchPointsForRound,
} from '../utils/tileMatchPlayScoring';
import { useQuizTimer } from '../hooks/useQuizTimer';
import {
  getTileMatchBoardBounds,
  TILE_MATCH_TILE_GRID_SIZE,
} from '../utils/tileMatchEngine';
import { tileMatchTilePanelStyle } from '../utils/quizQuestionPanelBackground';
import {
  playQuizSfxCorrect,
  playQuizSfxIncorrect,
  playTileMatchPickSfx,
} from '../audio/quizSfx';
import { useTileMatchWasm } from '../hooks/useTileMatchWasm';

import './shared/QuizStyles.css';
import './QuizPlayer.css';
import './TileMatchQuizPlayer.css';

const BOARD_OFFSET_PX = 12;
const BOARD_LAYOUT_NARROW_MAX_PX = 520;
const BOARD_CELL_PX_MOBILE_MIN = 34;
const BOARD_CELL_PX_MOBILE_MAX = 46;
const BOARD_CELL_PX_DESKTOP_MIN = 30;
const BOARD_CELL_PX_DESKTOP_MAX = 52;

function computeBoardCellPx(
  availW: number,
  spanX: number,
  spanY: number,
  isNarrow: boolean
): number {
  const pad = BOARD_OFFSET_PX * 2;
  const fitW = spanX > 0 ? Math.floor((availW - pad) / spanX) : BOARD_CELL_PX_DESKTOP_MIN;
  const minCell = isNarrow ? BOARD_CELL_PX_MOBILE_MIN : BOARD_CELL_PX_DESKTOP_MIN;
  const maxCell = isNarrow ? BOARD_CELL_PX_MOBILE_MAX : BOARD_CELL_PX_DESKTOP_MAX;
  const maxBoardH = isNarrow
    ? Math.min(window.innerHeight * 0.45, 340)
    : Math.min(window.innerHeight * 0.55, 520);
  const fitH = spanY > 0 ? Math.floor((maxBoardH - pad) / spanY) : maxCell;
  return Math.min(maxCell, Math.max(minCell, Math.min(fitW, fitH)));
}

export interface TileMatchPvpFinishPayload {
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  duration: number;
  questionIdsInOrder: string[];
  responseTimeSecondsPerQuestion: number[];
  correctPerQuestion: boolean[];
  tileMatchFinishAudit?: TileMatchFinishAudit;
  tileMatchResults?: Array<{ pointsEarned: number }>;
}

export interface TileMatchQuizPlayerProps {
  quizId: string;
  quiz: Quiz;
  tileMatch: TileMatchPlayStart;
  startTime: Date;
  onPlayAgain?: () => void | Promise<void>;
  playAgainLoading?: boolean;
  embeddedInPvp?: boolean;
  onPvpQuitRequest?: () => void;
  pvpAsync?: {
    flow: 'host' | 'opponent';
    roomId: string;
    onComplete: (payload: TileMatchPvpFinishPayload) => Promise<void>;
  };
  arenaPublishSnapshotRef?: React.MutableRefObject<SoloArenaPublishSnapshot | null>;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishCheckedRef?: React.MutableRefObject<boolean>;
}

const TileMatchQuizPlayer: React.FC<TileMatchQuizPlayerProps> = ({
  quizId,
  quiz,
  tileMatch,
  startTime,
  onPlayAgain,
  playAgainLoading = false,
  embeddedInPvp = false,
  onPvpQuitRequest,
  pvpAsync,
  arenaPublishSnapshotRef,
  arenaPublishChecked: arenaPublishCheckedProp,
  onArenaPublishCheckedChange: onArenaPublishCheckedChangeProp,
  arenaPublishCheckedRef: arenaPublishCheckedRefProp,
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const imageBase = resolveTileMatchImageAssetsFolder(
    quiz,
    tileMatch.imageAssetsBasePath
  );

  // WASM integration
  const {
    ready: wasmReady,
    error: wasmError,
    tryPickTile,
    applyPowerUp,
    getPlayState,
    buildFinishPayload,
    disposeSession,
  } = useTileMatchWasm(tileMatch.engineBootstrapCipher);

  const [currentRound, setCurrentRound] = useState(1);
  const [board, setBoard] = useState<TileMatchTileDto[]>([]);
  const [selectionBar, setSelectionBar] = useState<TileMatchTileDto[]>([]);
  const [evacuateHold, setEvacuateHold] = useState<TileMatchTileDto[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [pointsPerRound, setPointsPerRound] = useState<number[]>(
    () => Array(TILE_MATCH_TOTAL_ROUNDS).fill(0)
  );
  const [roundQuestionIds, setRoundQuestionIds] = useState<string[]>([]);

  const [shuffleUsed, setShuffleUsed] = useState(false);
  const [undoUsed, setUndoUsed] = useState(false);
  const [evacuateUsed, setEvacuateUsed] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [pickBusy, setPickBusy] = useState(false);
  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [resultDurationSeconds, setResultDurationSeconds] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [allRoundsWon, setAllRoundsWon] = useState(false);
  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);

  const [fallbackArenaPublishChecked, setFallbackArenaPublishChecked] = useState(true);
  const fallbackArenaPublishCheckedRef = useRef(true);
  const arenaPublishChecked = arenaPublishCheckedProp ?? fallbackArenaPublishChecked;
  const setArenaPublishChecked = onArenaPublishCheckedChangeProp ?? setFallbackArenaPublishChecked;
  const arenaPublishCheckedRef = arenaPublishCheckedRefProp ?? fallbackArenaPublishCheckedRef;

  const [hostedOpenSummaryForArena, setHostedOpenSummaryForArena] =
    useState<HostedOpenAsyncPvPSummary | null>(null);
  const [arenaHostedSummaryLoading, setArenaHostedSummaryLoading] = useState(false);

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked, arenaPublishCheckedRef]);

  const finishInFlightRef = useRef(false);
  const timeoutHandledRef = useRef(false);
  const roundStartedAtRef = useRef(Date.now());
  const secondsPerRoundRef = useRef<number[]>(
    Array.from({ length: TILE_MATCH_TOTAL_ROUNDS }, () => 0)
  );
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardCellPx, setBoardCellPx] = useState(BOARD_CELL_PX_DESKTOP_MIN);
  const [viewportTick, setViewportTick] = useState(0);

  const finishFlowActive =
    showCelebration ||
    levelUpCelebration.showLevelUp ||
    showResultsModal ||
    savingQuizAttempt ||
    showQuitModal;

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCelebrationPlayerNick('');
      return;
    }
    authService
      .getProfile()
      .then((u) => setCelebrationPlayerNick((u.nickname && u.nickname.trim()) || u.name || ''))
      .catch(() => setCelebrationPlayerNick(''));
  }, []);

  const progressPercent = (currentRound / TILE_MATCH_TOTAL_ROUNDS) * 100;

  const tileMatchRoundPoints = useMemo(
    () => tileMatchPointsForRound(currentRound - 1),
    [currentRound]
  );
  const correctRounds = pointsPerRound.filter((p) => p > 0).length;

  const arenaPublishOffer = useMemo(
    () =>
      isQuizCompleted &&
      TILE_MATCH_TOTAL_ROUNDS > 0 &&
      correctRounds / TILE_MATCH_TOTAL_ROUNDS >= 0.7,
    [isQuizCompleted, correctRounds]
  );

  const arenaPublishBlockedOpenQuiz = useMemo(() => {
    const qid = quiz?.id?.trim();
    if (!qid || !hostedOpenSummaryForArena?.openQuizIds?.length) return false;
    const lower = qid.toLowerCase();
    return hostedOpenSummaryForArena.openQuizIds.some((id) => id.trim().toLowerCase() === lower);
  }, [quiz?.id, hostedOpenSummaryForArena]);

  const arenaPublishBlockedMaxRooms = useMemo(() => {
    if (!hostedOpenSummaryForArena) return false;
    return hostedOpenSummaryForArena.openCount >= hostedOpenSummaryForArena.maxOpenRooms;
  }, [hostedOpenSummaryForArena]);

  const arenaPublishBlocked = arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms;

  const arenaPublishBlockedReason = arenaPublishBlockedOpenQuiz
    ? ('duplicateQuiz' as const)
    : arenaPublishBlockedMaxRooms
      ? ('maxRooms' as const)
      : undefined;

  useEffect(() => {
    if (arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms) {
      setArenaPublishChecked(false);
    }
  }, [arenaPublishBlockedOpenQuiz, arenaPublishBlockedMaxRooms, setArenaPublishChecked]);

  useEffect(() => {
    if (!showResultsModal || !arenaPublishOffer || !isQuizCompleted) {
      setHostedOpenSummaryForArena(null);
      setArenaHostedSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setArenaHostedSummaryLoading(true);
    void pvpService
      .getHostedOpenAsyncSummary()
      .then((s) => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(s);
          setArenaHostedSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(null);
          setArenaHostedSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showResultsModal, arenaPublishOffer, isQuizCompleted]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = arenaPublishSnapshotRef?.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Medium';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      if (arenaPublishSnapshotRef) arenaPublishSnapshotRef.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t, arenaPublishSnapshotRef, arenaPublishCheckedRef]);

  const recordRoundClearTime = useCallback((state: TileMatchPlayState) => {
    if (!state.isRoundWon) return;
    const completedRoundZero = state.nextRound
      ? state.currentRound - 2
      : state.currentRound - 1;
    if (completedRoundZero < 0 || completedRoundZero >= TILE_MATCH_TOTAL_ROUNDS) return;
    const elapsed = Math.min(
      TILE_MATCH_TIME_LIMIT_SECONDS,
      Math.max(0, Math.floor((Date.now() - roundStartedAtRef.current) / 1000))
    );
    secondsPerRoundRef.current[completedRoundZero] = elapsed;
    roundStartedAtRef.current = Date.now();
    setUndoUsed(false);
  }, []);

  useEffect(() => {
    roundStartedAtRef.current = Date.now();
  }, [currentRound]);

  const applyWasmState = useCallback((state: TileMatchPlayState) => {
    setCurrentRound(state.currentRound);
    setBoard(state.board.map((t) => ({ ...t })));
    setSelectionBar(state.selectionBar.map((t) => ({ ...t })));
    setEvacuateHold(state.evacuateHold.map((t) => ({ ...t })));
    setTotalScore(state.totalScore);
    setShuffleUsed(state.shuffleUsed);
    setUndoUsed(state.undoUsed);
    setEvacuateUsed(state.evacuateUsed);
    if (state.isGameOver) setIsGameOver(true);

    if (state.pointsPerRound?.length === TILE_MATCH_TOTAL_ROUNDS) {
      setPointsPerRound([...state.pointsPerRound]);
    } else if (state.pointsEarnedThisRound > 0) {
      setPointsPerRound((prev) => {
        const next = [...prev];
        const idx = state.currentRound - (state.nextRound ? 1 : 0) - 1;
        const roundIdx = Math.max(0, Math.min(TILE_MATCH_TOTAL_ROUNDS - 1, idx));
        next[roundIdx] = state.pointsEarnedThisRound;
        return next;
      });
    }

    if (state.nextRound?.tiles?.length) {
      setBoard(state.nextRound.tiles.map((t) => ({ ...t })));
      setSelectionBar([]);
      setCurrentRound(state.nextRound.roundIndex);
      setUndoUsed(false);

      const rep = state.nextRound.tiles[0]?.questionId;
      if (rep) setRoundQuestionIds((prev) => [...prev, rep]);
    } else if (state.board.length > 0) {
      // First round setup
      const rep = state.board[0]?.questionId;
      if (rep) {
        setRoundQuestionIds((prev) => (prev.length === 0 ? [rep] : prev));
      }
    }
  }, []);

  // Hydrate state once WASM is ready
  useEffect(() => {
    if (wasmReady) {
      getPlayState()
        .then(applyWasmState)
        .catch((e) => toast.error('WASM error: ' + e));
    }
  }, [wasmReady, getPlayState, applyWasmState]);

  const finishQuiz = useCallback(
    async (finalPoints: number[]) => {
      if (finishInFlightRef.current) return;
      finishInFlightRef.current = true;
      setSavingQuizAttempt(true);
      const secs = Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 1000));
      setResultDurationSeconds(secs);

      const questionIds = Array.from({ length: TILE_MATCH_TOTAL_ROUNDS }, (_, i) =>
        roundQuestionIds[i] ?? roundQuestionIds[roundQuestionIds.length - 1] ?? ''
      ).filter(Boolean);

      try {
        const witcResults = finalPoints.map((p) => ({ pointsEarned: p }));
        const total = finalPoints.reduce((a, b) => a + b, 0);
        const correct = finalPoints.filter((p) => p > 0).length;
        const wonAll = correct === TILE_MATCH_TOTAL_ROUNDS;
        const responseTimes = [...secondsPerRoundRef.current];

        const auditPayload = buildFinishPayload();
        if (!auditPayload) {
          finishInFlightRef.current = false;
          setIsQuizCompleted(false);
          setSaveAttemptError('Tile Match finish audit is missing. Reload the quiz and try again.');
          setSavingQuizAttempt(false);
          return;
        }

        if (pvpAsync) {
          try {
            await pvpAsync.onComplete({
              score: total,
              correctAnswers: correct,
              totalQuestions: TILE_MATCH_TOTAL_ROUNDS,
              duration: secs,
              questionIdsInOrder: questionIds,
              responseTimeSecondsPerQuestion: responseTimes,
              correctPerQuestion: finalPoints.map((p) => p > 0),
              tileMatchFinishAudit: auditPayload,
              tileMatchResults: witcResults,
            });
          } catch (e: unknown) {
            toast.error(t('quiz.saveFailed', 'Failed to save results.'));
            finishInFlightRef.current = false;
          } finally {
            setSavingQuizAttempt(false);
          }
          return;
        }

        const response = await quizService.saveQuizAttempt(
          {
            quizId,
            score: total,
            correctAnswers: correct,
            totalQuestions: TILE_MATCH_TOTAL_ROUNDS,
            duration: secs,
            isCompleted: true,
            answers: questionIds.map(() => 'tilematch'),
            questionIdsInOrder: questionIds,
            responseTimeSecondsPerQuestion: responseTimes,
            witcResults,
            tileMatchFinishAudit: auditPayload,
            playDifficulty: 'Medium',
          },
          { pwaClient: false }
        );
        setQuizResponse(response);
        if (response.earnedXP !== undefined) setEarnedXP(response.earnedXP);
        setTotalScore(total);
        setAllRoundsWon(wonAll);
        setIsQuizCompleted(true);

        if (arenaPublishSnapshotRef) {
          arenaPublishSnapshotRef.current = null;
          if (
            questionIds.length > 0 &&
            correct / questionIds.length >= 0.7 &&
            quiz
          ) {
            arenaPublishSnapshotRef.current = {
              quizId,
              playDifficulty: 'Medium',
              roomDisplayName: (quiz.title || 'Arena').trim().slice(0, 80),
              score: total,
              correctAnswers: correct,
              totalQuestions: questionIds.length,
              durationSeconds: secs,
              skippedQuestions: 0,
              questionIdsInOrder: questionIds,
              answerIdsPerQuestion: questionIds.map(() => []),
              responseTimeSecondsPerQuestion: responseTimes,
              correctPerQuestion: finalPoints.map((p) => p > 0),
              tileMatchFinishAudit: auditPayload ?? undefined,
              tileMatchResults: witcResults,
            };
          }
        }
        setArenaPublishChecked(true);

        const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
        const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
        const streakUpdate = response.loginStreakUpdated ?? null;
        const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
        if (unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate) {
          setCelebrationAchievements(unlocked);
          setCelebrationCharacters(unlockedChars);
          setCelebrationLoginStreak(streakUpdate);
          setShowCelebration(true);
        } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
          /* level-up modal */
        } else {
          setShowResultsModal(true);
        }
      } catch (err: unknown) {
        const msg =
          axios.isAxiosError(err) &&
          err.response?.data &&
          typeof (err.response.data as { message?: string }).message === 'string'
            ? (err.response.data as { message: string }).message
            : t('quiz.saveFailed', 'Failed to save results.');
        setSaveAttemptError(msg);
        finishInFlightRef.current = false;
      } finally {
        setSavingQuizAttempt(false);
      }
    },
    [
      quizId,
      roundQuestionIds,
      startTime,
      t,
      buildFinishPayload,
      arenaPublishSnapshotRef,
      setArenaPublishChecked,
      quiz,
      pvpAsync,
      levelUpCelebration,
    ]
  );

  const handleCelebrationFinished = useCallback(() => {
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  }, [levelUpCelebration]);

  const handleLevelUpFinished = useCallback(() => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  }, [levelUpCelebration]);

  const handleBackToHome = useCallback(() => {
    void executeArenaPublishFromSoloIfNeeded().then(() => {
      setShowResultsModal(false);
      setShowCelebration(false);
      setCelebrationAchievements([]);
      setCelebrationCharacters([]);
      setCelebrationLoginStreak(null);
      levelUpCelebration.clearPendingLevelUp();
      navigate('/', { replace: true });
    });
  }, [navigate, executeArenaPublishFromSoloIfNeeded, levelUpCelebration]);

  const finishPartialRunRef = useRef<(pts: number[]) => Promise<void>>(async () => {});

  const timerActive =
    wasmReady &&
    !isGameOver &&
    !savingQuizAttempt &&
    !finishFlowActive &&
    !finishInFlightRef.current &&
    !showQuitModal;

  const handleTimeout = useCallback(() => {
    if (timeoutHandledRef.current || finishInFlightRef.current) return;
    timeoutHandledRef.current = true;
    void finishPartialRunRef.current([...pointsPerRound]);
  }, [pointsPerRound]);

  const { timeLeft, stopTimer, addTime, resetTimer } = useQuizTimer({
    timeLimit: TILE_MATCH_TIME_LIMIT_SECONDS,
    onTimeout: handleTimeout,
    isActive: timerActive,
  });

  const handleQuitQuiz = () => {
    if (embeddedInPvp && onPvpQuitRequest) {
      onPvpQuitRequest();
      return;
    }
    setShowQuitModal(true);
  };

  const confirmQuit = useCallback(() => {
    stopTimer();
    setShowQuitModal(false);
    setIsGameOver(true);
    setResultDurationSeconds(
      Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 1000))
    );
    setIsQuizCompleted(false);
    setAllRoundsWon(false);
    if (arenaPublishSnapshotRef) arenaPublishSnapshotRef.current = null;
    setShowResultsModal(true);
  }, [startTime, stopTimer, arenaPublishSnapshotRef]);

  const finishPartialRun = useCallback(
    async (pts: number[]) => {
      if (finishInFlightRef.current) return;
      stopTimer();
      setIsGameOver(true);
      playQuizSfxIncorrect();
      setPointsPerRound(pts);
      await finishQuiz(pts);
    },
    [finishQuiz, stopTimer]
  );

  useEffect(() => {
    finishPartialRunRef.current = finishPartialRun;
  }, [finishPartialRun]);

  const handleRunLost = useCallback(
    async (state: TileMatchPlayState) => {
      const pts =
        state.pointsPerRound?.length === TILE_MATCH_TOTAL_ROUNDS
          ? [...state.pointsPerRound]
          : [...pointsPerRound];
      if (state.totalScore > 0) setTotalScore(state.totalScore);
      await finishPartialRun(pts);
    },
    [finishPartialRun, pointsPerRound]
  );

  const handlePick = async (tileId: string) => {
    if (pickBusy || isGameOver || savingQuizAttempt || !wasmReady) return;

    playTileMatchPickSfx();
    setPickBusy(true);

    const barLenBefore = selectionBar.length;

    try {
      const state = await tryPickTile(tileId);
      applyWasmState(state);
      recordRoundClearTime(state);

      if (state.selectionBar.length < barLenBefore) {
        addTime(TILE_MATCH_TRIO_TIME_BONUS_SECONDS);
      }

      if (state.isGameOver) {
        await handleRunLost(state);
        return;
      }

      if (state.isRoundWon && !state.sessionComplete) {
        playQuizSfxCorrect();
        timeoutHandledRef.current = false;
        resetTimer();
        return;
      }

      if (state.sessionComplete) {
        stopTimer();
        playQuizSfxCorrect();
        const pts = [...pointsPerRound];
        const lastIdx = currentRound - 1;
        if (state.pointsEarnedThisRound > 0 && lastIdx >= 0 && lastIdx < TILE_MATCH_TOTAL_ROUNDS) {
          pts[lastIdx] = state.pointsEarnedThisRound;
        }
        await finishQuiz(pts);
        return;
      }
    } catch (err: unknown) {
      toast.error(t('tileMatch.pickFailed', 'Could not pick tile.'));
    } finally {
      setPickBusy(false);
    }
  };

  const handlePowerUp = async (powerUp: 'shuffle' | 'undo' | 'evacuate') => {
    if (pickBusy || isGameOver || savingQuizAttempt || !wasmReady) return;
    setPickBusy(true);
    try {
      const state = await applyPowerUp(powerUp);
      applyWasmState(state);
      recordRoundClearTime(state);

      if (state.isGameOver) {
        await handleRunLost(state);
        return;
      }

      if (state.isRoundWon && !state.sessionComplete) {
        playQuizSfxCorrect();
        timeoutHandledRef.current = false;
        resetTimer();
        return;
      }

      if (state.sessionComplete) {
        stopTimer();
        const pts = [...pointsPerRound];
        const lastIdx = currentRound - 1;
        if (state.pointsEarnedThisRound > 0 && lastIdx >= 0) pts[lastIdx] = state.pointsEarnedThisRound;
        await finishQuiz(pts);
      }
    } catch (err: unknown) {
      toast.error(t('tileMatch.powerUpFailed', 'Power-up failed.'));
    } finally {
      setPickBusy(false);
    }
  };

  const boardBounds = useMemo(() => getTileMatchBoardBounds(board), [board]);

  useEffect(() => {
    const onResize = () => setViewportTick((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    const wrap = boardAreaRef.current;
    if (!wrap || board.length === 0) {
      setBoardCellPx(BOARD_CELL_PX_DESKTOP_MIN);
      return;
    }
    const availW = wrap.clientWidth;
    const isNarrow = availW < BOARD_LAYOUT_NARROW_MAX_PX;
    setBoardCellPx(
      computeBoardCellPx(availW, boardBounds.spanX, boardBounds.spanY, isNarrow)
    );
  }, [board.length, boardBounds, currentRound, viewportTick]);

  const boardTilePx = boardCellPx * TILE_MATCH_TILE_GRID_SIZE;
  const boardWidth = boardBounds.spanX * boardCellPx + BOARD_OFFSET_PX * 2;
  const boardHeight = boardBounds.spanY * boardCellPx + BOARD_OFFSET_PX * 2;
  const boardTileImagePx = Math.max(28, boardTilePx - 10);

  const tileCardStyle = (imageAssetKey: string): React.CSSProperties => {
    const { backgroundColor, borderColor } = tileMatchTilePanelStyle(imageAssetKey);
    return {
      backgroundColor,
      borderColor,
      borderWidth: 2,
      borderStyle: 'solid',
    };
  };

  const renderTileImage = (tile: TileMatchTileDto, size?: number) => {
    const urls = quizQuestionImageUrlsFromKey(imageBase, tile.imageAssetKey);
    if (!urls) {
      return (
        <span
          className="small text-muted text-truncate"
          style={size != null ? { maxWidth: size } : undefined}
        >
          {tile.imageAssetKey}
        </span>
      );
    }
    return (
      <img
        src={urls.webp}
        alt=""
        {...(size != null ? { width: size, height: size } : {})}
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={(e) => {
          const img = e.currentTarget;
          if (img.src !== urls.png) img.src = urls.png;
        }}
      />
    );
  };

  if (saveAttemptError) {
    return (
      <div className="quiz-page d-flex flex-column align-items-center justify-content-center min-vh-50 text-white">
        <Alert variant="danger" className="mx-3 text-center">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{saveAttemptError}</p>
          <Button variant="outline-danger" onClick={() => navigate('/', { replace: true })}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  // Show loading spinner if WASM is not yet initialized
  if (!wasmReady) {
    if (wasmError) {
      return (
        <div className="quiz-page d-flex flex-column align-items-center justify-content-center min-vh-50 text-white">
          <Alert variant="danger" className="mx-3 text-center">
            {wasmError}
          </Alert>
        </div>
      );
    }
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-page tile-match-quiz-player">
      <div className="quiz-container">
        <div className="quiz-header quiz-page-header">
          <QuizPlayPageHeading quiz={quiz} language={i18n.language} />
          <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
              <Badge bg="warning">{t('quiz.medium', 'Medium')}</Badge>
              <span className="text-muted quiz-page-header__question-of">
                {t('tileMatch.roundOf', 'Round {{current}} of {{total}}')
                  .replace('{{current}}', String(currentRound))
                  .replace('{{total}}', String(TILE_MATCH_TOTAL_ROUNDS))}
              </span>
              <Badge
                bg={timeLeft <= 10 ? 'danger' : timeLeft <= 20 ? 'warning' : 'info'}
                className="timer-badge"
              >
                {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <NavbarMusicToggle className="flex-shrink-0" />
              <QuizSoundToggleButton disabled={savingQuizAttempt} />
              <Button
                type="button"
                variant="outline-danger"
                onClick={handleQuitQuiz}
                className="quiz-quit-icon-btn flex-shrink-0"
                aria-label={t('quiz.quitQuiz', 'Quit Quiz')}
                disabled={savingQuizAttempt}
              >
                <i className="bi bi-x-lg" aria-hidden />
              </Button>
            </div>
          </div>
        </div>

        <ProgressBar
          now={progressPercent}
          className="quiz-progress-bar"
          label={`${Math.round(progressPercent)}%`}
        />

        <Card className="question-card quiz-compact mb-3">
          <Card.Body>
            <Card.Title className="question-title h5 mb-3">
              {t('tileMatch.instruction', 'Match three identical tiles in the bar')}
            </Card.Title>
            <p className="small mb-2 fw-medium quiz-points-hint witc-points-left--full">
              {i18n.t('tileMatch.pointsForRound', { count: tileMatchRoundPoints })}
            </p>

            {isGameOver && !finishFlowActive ? (
              <Alert variant="danger" className="mb-3">
                {t('tileMatch.gameOverBody', 'The selection bar filled up. Your run has ended.')}
              </Alert>
            ) : null}

            <div ref={boardAreaRef} className="tile-match-board-area">
              <div
                className="tile-match-board"
                style={{ width: boardWidth, height: boardHeight, maxWidth: '100%' }}
              >
                {[...board]
                  .sort((a, b) => a.z - b.z)
                  .map((tile) => (
                    <button
                      key={tile.id}
                      type="button"
                      className={`tile-match-tile${tile.isBlocked ? ' tile-match-tile--blocked' : ''}`}
                      style={{
                        left: BOARD_OFFSET_PX + (tile.x - boardBounds.minX) * boardCellPx,
                        top: BOARD_OFFSET_PX + (tile.y - boardBounds.minY) * boardCellPx,
                        width: boardTilePx,
                        height: boardTilePx,
                        zIndex: 10 + tile.z,
                        ...tileCardStyle(tile.imageAssetKey),
                      }}
                      disabled={tile.isBlocked || pickBusy || isGameOver}
                      aria-disabled={tile.isBlocked || pickBusy || isGameOver}
                      onClick={() => {
                        if (tile.isBlocked || pickBusy || isGameOver) return;
                        void handlePick(tile.id);
                      }}
                      aria-label={tile.imageAssetKey}
                    >
                      {renderTileImage(tile, boardTileImagePx)}
                    </button>
                  ))}
              </div>
            </div>

            <div className="tile-match-bar-outer">
              <div
                className="tile-match-bar"
                aria-label={t('tileMatch.selectionBar', 'Selection bar')}
              >
                <div className="tile-match-bar-inner" role="list">
                  {Array.from({ length: TILE_MATCH_BAR_CAPACITY }).map((_, i) => {
                    const tile = selectionBar[i];
                    return (
                      <div
                        key={i}
                        className={`tile-match-bar-slot${tile ? ' tile-match-bar-slot--filled' : ''}`}
                        role="listitem"
                        style={tile ? tileCardStyle(tile.imageAssetKey) : undefined}
                      >
                        {tile ? renderTileImage(tile) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="tile-match-undo-row">
              <Button
                variant="outline-secondary"
                onClick={() => void handlePowerUp('undo')}
                className="tile-match-undo-btn d-inline-flex align-items-center"
                disabled={undoUsed || pickBusy || isGameOver || savingQuizAttempt}
              >
                <i className="bi bi-arrow-counterclockwise me-1 me-sm-2" aria-hidden />
                {t('tileMatch.undo', 'Undo')}
              </Button>
            </div>

            <div className="quiz-stats-actions-container tile-match-stats-row">
              <div className="quiz-stats-inline">
                <div className="stat-line">
                  {t('quiz.score', 'Score')}: {totalScore}
                </div>
                <div className="stat-line">
                  {t('quiz.correctAnswers', 'Correct')}: {correctRounds}/{TILE_MATCH_TOTAL_ROUNDS}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {savingQuizAttempt ? (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">{t('quiz.savingResults', 'Saving results...')}</span>
          </div>
        </div>
      ) : null}

      <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('quiz.quitQuizModal', 'Quit Quiz?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('quiz.quitQuizBody', 'Your progress will be lost.')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmQuit}>
            {t('quiz.quitConfirm', 'Quit')}
          </Button>
        </Modal.Footer>
      </Modal>

      <QuizFinishCelebrationModal
        show={showCelebration}
        onFinished={handleCelebrationFinished}
        achievements={celebrationAchievements}
        characters={celebrationCharacters}
        loginStreak={celebrationLoginStreak}
        playerNickname={celebrationPlayerNick}
      />

      <LevelUpCelebrationModal
        show={levelUpCelebration.showLevelUp}
        oldLevel={levelUpCelebration.levelUpOld}
        newLevel={levelUpCelebration.levelUpNew}
        onClose={handleLevelUpFinished}
      />

      <SoloQuizResultsModal
        show={showResultsModal}
        hasTooManySkips={false}
        isQuizCompleted={isQuizCompleted}
        finishedAsGameOver={isQuizCompleted && !allRoundsWon}
        quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
        correctAnswers={quizResponse?.correctAnswers ?? correctRounds}
        totalQuestions={TILE_MATCH_TOTAL_ROUNDS}
        score={totalScore}
        earnedXP={earnedXP}
        quizResponse={quizResponse}
        resultDurationSeconds={resultDurationSeconds}
        onPlayAgain={onPlayAgain ?? (() => {})}
        onBackToHome={handleBackToHome}
        playAgainLoading={playAgainLoading}
        arenaPublishOffer={arenaPublishOffer}
        arenaPublishChecked={arenaPublishChecked}
        onArenaPublishCheckedChange={setArenaPublishChecked}
        arenaPublishAvailabilityLoading={arenaHostedSummaryLoading}
        arenaPublishBlocked={arenaPublishBlocked}
        arenaPublishBlockedReason={arenaPublishBlockedReason}
        arenaPublishMaxOpenRooms={hostedOpenSummaryForArena?.maxOpenRooms}
      />
    </div>
  );
};

export default TileMatchQuizPlayer;
